import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  BookOpen, 
  CreditCard, 
  Activity, 
  ShieldCheck, 
  Download,
  Server,
  Zap,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Layout,
  Database,
  Tag,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Plus,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import Button from "../../components/common/Button";
import { 
  CompanyPriceListItem, 
  PriceListAnalytics 
} from "../../services/priceListService";
import { usePriceListQuery } from "../../hooks/queries/usePriceListQueries";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [chartView, setChartView] = useState('revenue'); // 'revenue' or 'users'
  const { data: priceListResponse, isLoading: loadingPriceList } = usePriceListQuery();
  const priceListItems = useMemo(
    () => (priceListResponse?.data ? priceListResponse.data.slice(0, 5) : []),
    [priceListResponse]
  );
  const priceAnalytics = priceListResponse?.analytics || null;

  return (
    <div className="cht-space-y-10 cht-animate-in cht-fade-in cht-duration-700">
      
      {/* SuperAdmin Hero */}
      <header className="cht-relative cht-overflow-hidden cht-rounded-[40px] cht-bg-[#1e293b] cht-p-10 md:cht-p-16 cht-text-white">
        <div className="cht-relative cht-z-10 cht-max-w-2xl cht-space-y-6">
          <div className="cht-inline-flex cht-items-center cht-gap-2 cht-px-4 cht-py-1.5 cht-bg-white/10 cht-backdrop-blur-md cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest">
            <ShieldCheck size={12} className="cht-text-emerald-400" /> SuperAdmin Console
          </div>
          <h1 className="cht-text-4xl md:cht-text-5xl cht-font-black cht-tracking-tight cht-leading-tight">
            Global Platform <span className="cht-text-[#3e57da]">Command Center</span>.
          </h1>
          <p className="cht-text-gray-300 cht-font-medium cht-text-lg">
            System status is optimal. You have 3 critical security logs and 12 instructor payouts pending approval.
          </p>
          <div className="cht-flex cht-flex-wrap cht-gap-4 cht-pt-4">
            <Button 
              variant="primary" 
              className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-2xl cht-px-8"
              leftIcon={<Download size={18} />}
            >
              Export Global Report
            </Button>
            <Button 
              variant="outline" 
              className="cht-border-white/20 cht-text-white hover:cht-bg-white/10 cht-rounded-2xl cht-px-8"
            >
              System Settings
            </Button>
          </div>
        </div>
        <div className="cht-absolute cht-right-0 cht-top-0 cht-w-1/2 cht-h-full cht-opacity-10 cht-pointer-events-none">
          <Layout className="cht-w-full cht-h-full cht-scale-150 -cht-rotate-12" />
        </div>
      </header>

      {/* Global KPIs */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        {[
          { label: "Total Revenue (MTD)", value: "₦4,240,500", icon: CreditCard, color: "blue", trend: "+18%" },
          { label: "Active Users", value: "12,840", icon: Users, color: "purple", trend: "+2.4%" },
          { label: "Server Load", value: "34%", icon: Server, color: "emerald", trend: "Optimal" },
          { label: "System Uptime", value: "99.99%", icon: Activity, color: "amber", trend: "Stable" }
        ].map((stat, i) => (
          <GlassCard key={i} className="cht-p-8 cht-rounded-[32px] cht-space-y-4 cht-group hover:cht-border-[#3e57da]/20 cht-transition-all">
            <div className={`cht-w-12 cht-h-12 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-bg-${stat.color}-50 cht-text-${stat.color}-500 group-hover:cht-scale-110 cht-transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div className="cht-space-y-1">
              <div className="cht-flex cht-items-center cht-justify-between">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">{stat.label}</p>
                <span className={`cht-text-[10px] cht-font-black ${stat.trend.includes('+') || stat.trend === 'Optimal' || stat.trend === 'Stable' ? 'cht-text-emerald-500' : 'cht-text-rose-500'}`}>{stat.trend}</span>
              </div>
              <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">{stat.value}</h4>
            </div>
          </GlassCard>
        ))}
      </div>

      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8">
        
        {/* Dynamic Growth Chart */}
        <GlassCard className="lg:cht-col-span-8 cht-p-10 cht-rounded-[40px] cht-flex cht-flex-col">
          <div className="cht-flex cht-items-center cht-justify-between cht-mb-10">
            <div className="cht-space-y-1">
              <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-tracking-tight">Platform Analytics</h3>
              <p className="cht-text-xs cht-text-gray-400 cht-font-medium">Global metrics over the last 12 months</p>
            </div>
            <div className="cht-flex cht-gap-2 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
              <button 
                onClick={() => setChartView('revenue')}
                className={`cht-px-4 cht-py-2 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${chartView === 'revenue' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400 hover:cht-text-gray-600'}`}
              >
                Revenue
              </button>
              <button 
                onClick={() => setChartView('users')}
                className={`cht-px-4 cht-py-2 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${chartView === 'users' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400 hover:cht-text-gray-600'}`}
              >
                Users
              </button>
            </div>
          </div>
          
          <div className="cht-flex-1 cht-min-h-[300px] cht-relative cht-flex cht-items-end cht-gap-4">
            {(chartView === 'revenue' 
              ? [30, 45, 40, 60, 55, 70, 65, 85, 80, 95, 90, 100] 
              : [10, 15, 25, 30, 40, 50, 60, 70, 75, 85, 90, 100]
            ).map((h, i) => (
              <motion.div 
                key={`${chartView}-${i}`}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ delay: i * 0.05, duration: 0.8, type: "spring" }}
                className={`cht-flex-1 cht-rounded-t-lg cht-relative cht-group ${chartView === 'revenue' ? 'cht-bg-[#3e57da]/10' : 'cht-bg-emerald-500/10'}`}
              >
                <div className={`cht-absolute cht-inset-x-0 cht-bottom-0 cht-h-full cht-rounded-t-lg cht-scale-y-0 group-hover:cht-scale-y-100 cht-origin-bottom cht-transition-transform cht-duration-500 ${chartView === 'revenue' ? 'cht-bg-[#3e57da]' : 'cht-bg-emerald-500'}`} />
              </motion.div>
            ))}
          </div>
          <div className="cht-flex cht-justify-between cht-mt-4 cht-text-[9px] cht-font-bold cht-text-gray-400 cht-uppercase">
            <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span><span>Jul</span><span>Aug</span><span>Sep</span><span>Oct</span><span>Nov</span><span>Dec</span>
          </div>
        </GlassCard>

        {/* Security & System Events */}
        <div className="lg:cht-col-span-4 cht-space-y-8">
          <GlassCard className="cht-p-8 cht-rounded-[40px] cht-space-y-6">
            <h3 className="cht-text-sm cht-font-black cht-uppercase cht-tracking-widest">Live Security Feed</h3>
            <div className="cht-space-y-6">
              {[
                { type: "critical", msg: "Multiple failed login attempts", loc: "IP: 192.168.1.1", time: "2m ago", icon: AlertTriangle },
                { type: "warning", msg: "Unusual data export requested", loc: "Admin: Sarah Jen", time: "15m ago", icon: ShieldCheck },
                { type: "info", msg: "System backup completed", loc: "Server: US-East", time: "1h ago", icon: Database }
              ].map((event, i) => (
                <div key={i} className="cht-flex cht-items-start cht-gap-4">
                  <div className={`cht-w-10 cht-h-10 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-flex-shrink-0 ${
                    event.type === 'critical' ? 'cht-bg-rose-50 cht-text-rose-500' :
                    event.type === 'warning' ? 'cht-bg-amber-50 cht-text-amber-500' :
                    'cht-bg-blue-50 cht-text-blue-500'
                  }`}>
                    <event.icon size={18} />
                  </div>
                  <div className="cht-flex-1 cht-min-w-0 cht-pt-1">
                    <p className="cht-text-sm cht-font-bold cht-text-gray-900 cht-truncate">{event.msg}</p>
                    <p className="cht-text-[10px] cht-text-gray-500 cht-font-bold">{event.loc}</p>
                  </div>
                  <span className="cht-text-[9px] cht-font-bold cht-text-gray-300 cht-uppercase cht-pt-1">{event.time}</span>
                </div>
              ))}
            </div>
            <Button variant="ghost" className="cht-w-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-pt-4">View Full Logs</Button>
          </GlassCard>

          <GlassCard className="cht-p-8 cht-rounded-[40px] cht-bg-gradient-to-br cht-from-[#1e293b] cht-to-black cht-text-white cht-space-y-4">
            <h3 className="cht-text-sm cht-font-black cht-uppercase cht-tracking-widest cht-text-gray-400">Action Required</h3>
            <div className="cht-space-y-4">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <CreditCard size={18} className="cht-text-emerald-400" />
                  <span className="cht-text-sm cht-font-bold">12 Instructor Payouts</span>
                </div>
                <ChevronRight size={16} className="cht-opacity-50" />
              </div>
              <p className="cht-text-[11px] cht-text-gray-400">End of month settlements are awaiting your final approval.</p>
              <Button 
                variant="primary" 
                className="cht-w-full cht-bg-emerald-500 hover:cht-bg-emerald-600 cht-border-none cht-rounded-xl cht-mt-2"
              >
                Review Payouts
              </Button>
            </div>
          </GlassCard>
        </div>
      </div>

      {/* Company Price Catalog & Deliverables Section */}
      <GlassCard className="cht-p-8 md:cht-p-10 cht-rounded-[40px] cht-space-y-6">
        <div className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-4">
          <div className="cht-flex cht-items-center cht-gap-4">
            <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-flex-shrink-0">
              <Tag size={24} />
            </div>
            <div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-tracking-tight">Company Price Catalog</h3>
                <span className="cht-px-2.5 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase">
                  Live Rates
                </span>
              </div>
              <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                Standard client rates, deliverable fulfillment timelines, and SuperAdmin moderation status.
              </p>
            </div>
          </div>
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/staff/dashboard/price-list")}
              className="cht-rounded-xl cht-text-xs cht-font-bold"
            >
              Propose Item <Plus size={14} className="cht-ml-1" />
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate("/staff/dashboard/price-list")}
              className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold cht-flex cht-items-center cht-gap-1.5"
            >
              Manage Catalog <ArrowUpRight size={14} />
            </Button>
          </div>
        </div>

        {/* Quick KPI Strip */}
        <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 cht-gap-4 cht-pt-2">
          <div className="cht-p-4 cht-bg-gray-50/70 cht-rounded-2xl cht-border cht-border-gray-100">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Active Deliverables</p>
            <p className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.total_live_items ?? 0}
            </p>
          </div>
          <div className="cht-p-4 cht-bg-gray-50/70 cht-rounded-2xl cht-border cht-border-gray-100">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Service Categories</p>
            <p className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.categories?.length ?? 0}
            </p>
          </div>
          <div className="cht-p-4 cht-bg-amber-50/50 cht-rounded-2xl cht-border cht-border-amber-100/60 cht-col-span-2 sm:cht-col-span-1">
            <p className="cht-text-[10px] cht-font-black cht-text-amber-700 cht-uppercase cht-tracking-widest">In SuperAdmin Review</p>
            <p className="cht-text-xl cht-font-black cht-text-amber-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.total_pending_queue ?? 0}
            </p>
          </div>
        </div>

        {/* Catalog Preview List */}
        <div className="cht-overflow-hidden cht-rounded-2xl cht-border cht-border-gray-100 cht-bg-white">
          {loadingPriceList ? (
            <div className="cht-p-8 cht-space-y-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="cht-h-12 cht-bg-gray-100 cht-rounded-xl cht-animate-pulse" />
              ))}
            </div>
          ) : priceListItems.length === 0 ? (
            <div className="cht-p-10 cht-text-center cht-space-y-3">
              <Tag size={32} className="cht-mx-auto cht-text-gray-300" />
              <p className="cht-text-sm cht-font-bold cht-text-gray-700">No price catalog items found</p>
              <p className="cht-text-xs cht-text-gray-400">Propose items to populate standard company deliverable rates.</p>
              <Button
                variant="outline"
                onClick={() => navigate("/staff/dashboard/price-list")}
                className="cht-rounded-xl cht-text-xs cht-font-bold cht-mt-2"
              >
                Create Proposal
              </Button>
            </div>
          ) : (
            <div className="cht-overflow-x-auto">
              <table className="cht-w-full cht-text-left cht-text-xs">
                <thead>
                  <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50 cht-text-gray-400 cht-font-black cht-uppercase cht-tracking-wider">
                    <th className="cht-py-3.5 cht-px-4">Deliverable / Item</th>
                    <th className="cht-py-3.5 cht-px-4">Category</th>
                    <th className="cht-py-3.5 cht-px-4">Standard Rate</th>
                    <th className="cht-py-3.5 cht-px-4">Turnaround</th>
                    <th className="cht-py-3.5 cht-px-4">Moderation Status</th>
                    <th className="cht-py-3.5 cht-px-4 cht-text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="cht-divide-y cht-divide-gray-50 cht-font-medium">
                  {priceListItems.map((item) => (
                    <tr key={item.id} className="hover:cht-bg-gray-50/80 cht-transition-colors">
                      <td className="cht-py-3.5 cht-px-4">
                        <div className="cht-font-black cht-text-gray-900">{item.name}</div>
                        <div className="cht-text-[10px] cht-font-bold cht-text-gray-400">{item.item_code}</div>
                      </td>
                      <td className="cht-py-3.5 cht-px-4">
                        <span className="cht-px-2.5 cht-py-1 cht-bg-gray-100 cht-rounded-md cht-text-[10px] cht-font-bold cht-text-gray-600">
                          {item.category}
                        </span>
                      </td>
                      <td className="cht-py-3.5 cht-px-4">
                        <span className="cht-font-black cht-text-gray-900">
                          ₦{Number(item.standard_price).toLocaleString()}
                        </span>
                      </td>
                      <td className="cht-py-3.5 cht-px-4">
                        <span className="cht-text-gray-600 cht-flex cht-items-center cht-gap-1">
                          <Clock size={12} className="cht-text-gray-400" />
                          {item.delivery_timeline || "Standard"}
                        </span>
                      </td>
                      <td className="cht-py-3.5 cht-px-4">
                        {item.change_status && item.change_status !== 'NONE' ? (
                          <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200">
                            {item.change_status_display}
                          </span>
                        ) : item.status === 'APPROVED' ? (
                          <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 cht-inline-flex cht-items-center cht-gap-1">
                            <CheckCircle2 size={10} /> Active
                          </span>
                        ) : (
                          <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200">
                            Pending Approval
                          </span>
                        )}
                      </td>
                      <td className="cht-py-3.5 cht-px-4 cht-text-right">
                        <button
                          onClick={() => navigate("/staff/dashboard/price-list")}
                          className="cht-text-[#3e57da] hover:cht-text-[#2d41a8] cht-font-bold cht-inline-flex cht-items-center cht-gap-1"
                        >
                          View <ChevronRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

export default AdminDashboard;
